import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TablePagination,
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { directivesApi } from "../services/api";
import { format } from "date-fns";

const DirectivePage = () => {
  const { t } = useTranslation();
  const [directiveInfo, setDirectiveInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: "",
    year: new Date().getFullYear(),
    version: "1.0",
    file: null,
  });
  const [uploadErrors, setUploadErrors] = useState({});
  const [directiveHistory, setDirectiveHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Fetch current directive info
  const fetchDirectiveInfo = async () => {
    try {
      if (!directiveInfo) setLoading(true);
      const response = await directivesApi.info();
      setDirectiveInfo(response.data.directive);
      setError("");
    } catch (err) {
      if (err.response?.status === 404) {
        setDirectiveInfo(null);
        setError(t('directive.noActiveFound'));
      } else {
        setError(err.response?.data?.message || t('directive.failedToLoad'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch directive history
  const fetchDirectiveHistory = async () => {
    try {
      if (directiveHistory.length === 0) setHistoryLoading(true);
      const response = await directivesApi.history();
      setDirectiveHistory(response.data.directives);
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchDirectiveInfo();
      await fetchDirectiveHistory();
    };
    loadData();
  }, []);

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        setUploadErrors({ ...uploadErrors, file: t('directive.onlyPdfAllowed') });
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setUploadErrors({ ...uploadErrors, file: t('directive.fileSizeLimit') });
        return;
      }
      setUploadForm({ ...uploadForm, file });
      setUploadErrors({ ...uploadErrors, file: "" });
    }
  };

  // Validate upload form
  const validateForm = () => {
    const errors = {};
    if (!uploadForm.title.trim()) errors.title = t('directive.titleRequired');
    if (!uploadForm.year || uploadForm.year < 2000 || uploadForm.year > 2100) {
      errors.year = t('directive.yearValidation');
    }
    if (!uploadForm.version.trim()) errors.version = t('directive.versionRequired');
    if (!uploadForm.file) errors.file = t('directive.fileRequired');

    setUploadErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle directive upload
  const handleUpload = async () => {
    if (!validateForm()) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("title", uploadForm.title);
      formData.append("year", uploadForm.year);
      formData.append("version", uploadForm.version);
      formData.append("file", uploadForm.file);

      await directivesApi.upload(formData);

      setSuccess(t('directive.uploadSuccess'));
      setUploadDialogOpen(false);
      setUploadForm({
        title: "",
        year: new Date().getFullYear(),
        version: "1.0",
        file: null,
      });
      fetchDirectiveInfo();
      fetchDirectiveHistory();
    } catch (err) {
      setError(err.response?.data?.message || t('directive.failedToUpload'));
    } finally {
      setUploading(false);
    }
  };

  // Handle directive view
  const handleViewDirective = async (id) => {
    try {
      const response = await directivesApi.view(id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      setError(err.response?.data?.message || t('directive.failedToView'));
    }
  };

  // Handle directive download
  const handleDownloadDirective = async (id, fileName) => {
    try {
      const response = await directivesApi.download(id || directiveInfo?.id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || directiveInfo?.fileName || "directive.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || t('directive.failedToDownload'));
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedHistory = directiveHistory.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('directive.managementTitle')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<UploadIcon />}
          onClick={() => setUploadDialogOpen(true)}
        >
          {t('directive.uploadNew')}
        </Button>
      </Box>

      {/* Error/Success Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Current Directive Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PdfIcon color="error" sx={{ fontSize: 40, mr: 2 }} />
            <Box>
              <Typography variant="h6" color="primary">
                {t('directive.currentActive')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('directive.visibleToStaff')}
              </Typography>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : directiveInfo ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle2" gutterBottom>{t('directive.selectFile')}</Typography>
                <Typography variant="h5" gutterBottom>
                  {directiveInfo.title}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                  <Chip
                    label={`${t('directive.yearLabel')}: ${directiveInfo.year}`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    label={`${t('directive.version')}: ${directiveInfo.version}`}
                    color="secondary"
                    variant="outlined"
                  />
                  <Chip
                    label={directiveInfo.fileSize}
                    icon={<InfoIcon />}
                    variant="outlined"
                  />
                  <Chip
                    label={`${t('common.registered')}: ${format(new Date(directiveInfo.uploadedAt), "MMM dd, yyyy")}`}
                    variant="outlined"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t('directive.uploadedBy')} <strong>{directiveInfo.uploadedBy}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <CardActions sx={{ justifyContent: "flex-end", flexDirection: { xs: "column", md: "row" }, gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewDirective(directiveInfo.id)}
                    fullWidth={window.innerWidth < 900}
                  >
                    {t('directive.viewDirective')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadDirective(directiveInfo.id, directiveInfo.fileName)}
                    fullWidth={window.innerWidth < 900}
                  >
                    {t('common.download')}
                  </Button>
                </CardActions>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">
              {t('directive.noActiveFound')}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Directive History */}
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h6">
              {t('directive.historyTitle')}
            </Typography>
            <IconButton onClick={fetchDirectiveHistory} disabled={historyLoading}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {historyLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : directiveHistory.length > 0 ? (
            <Box>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('directive.titleLabel')}</TableCell>
                      <TableCell>{t('directive.yearLabel')}</TableCell>
                      <TableCell>{t('directive.version')}</TableCell>
                      <TableCell>{t('directive.uploadedBy')}</TableCell>
                      <TableCell>{t('common.date')}</TableCell>
                      <TableCell>{t('common.status')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedHistory.map((directive) => (
                      <TableRow key={directive.id}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <PdfIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
                            {directive.title}
                          </Box>
                        </TableCell>
                        <TableCell>{directive.year}</TableCell>
                        <TableCell>{directive.version}</TableCell>
                        <TableCell>{directive.uploadedBy}</TableCell>
                        <TableCell>
                          {format(new Date(directive.uploadedAt), "MMM dd, yyyy")}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={directive.isActive ? t('directive.active') : t('directive.inactive')}
                            color={directive.isActive ? "success" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDirective(directive.id)}
                            title={t('common.view')}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadDirective(directive.id, directive.title + ".pdf")}
                            title={t('common.download')}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={directiveHistory.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage={t('common.rowsPerPage') || "Rows per page:"}
              />
            </Box>
          ) : (
            <Alert severity="info">
              {t('directive.noHistory')}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('directive.uploadDialogTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label={t('directive.titleLabel')}
              value={uploadForm.title}
              onChange={(e) => {
                setUploadForm({ ...uploadForm, title: e.target.value });
                setUploadErrors({ ...uploadErrors, title: "" });
              }}
              error={!!uploadErrors.title}
              helperText={uploadErrors.title}
              margin="normal"
              required
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('directive.yearLabel')}
                  type="number"
                  value={uploadForm.year}
                  onChange={(e) => {
                    setUploadForm({ ...uploadForm, year: e.target.value });
                    setUploadErrors({ ...uploadErrors, year: "" });
                  }}
                  error={!!uploadErrors.year}
                  helperText={uploadErrors.year || t('directive.yearHelper')}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('directive.versionLabel')}
                  value={uploadForm.version}
                  onChange={(e) => {
                    setUploadForm({ ...uploadForm, version: e.target.value });
                    setUploadErrors({ ...uploadErrors, version: "" });
                  }}
                  error={!!uploadErrors.version}
                  helperText={uploadErrors.version}
                  margin="normal"
                  required
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, mb: 2 }}>
              <input
                accept=".pdf"
                style={{ display: "none" }}
                id="directive-file-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="directive-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ height: 56 }}
                >
                  {uploadForm.file ? t('directive.selectedFile', { name: uploadForm.file.name }) : t('directive.selectPdfFile')}
                </Button>
              </label>
              {uploadForm.file && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  Size: {(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              )}
              {uploadErrors.file && (
                <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                  {uploadErrors.file}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {t('directive.maxFileSize')}
              </Typography>
            </Box>

            {uploadForm.file && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>{t('common.info')}:</strong> {t('directive.uploadWarning')}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            color="primary"
            disabled={uploading || !uploadForm.file}
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {uploading ? t('directive.uploading') : t('directive.upload')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DirectivePage;